'use client';

import { useRef, type PropsWithChildren } from 'react';

import { createContext } from '@backyard-ui/utils';
import { createStore, type StoreApi } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { type Response as GetCrossesProductsResponse } from '@/infra/services/catalog/product/get-crosses-products';
import { type Response as GetProductInfoResponse } from '@/infra/services/catalog/product/get-product-info';
import { type Seller } from '@/infra/services/catalog/product/get-sellers/model';
import { type Response as GetShipmentsResponse } from '@/infra/services/catalog/product/get-shipments';
import { type Response as GetVariantsResponse } from '@/infra/services/catalog/product/get-variants';

export interface ProductState {
  product: GetProductInfoResponse['data']['product'];
  category: GetProductInfoResponse['data']['category'];
  variants: GetVariantsResponse['data'];
  crossesProducts?: GetCrossesProductsResponse['data'];
  currentSeller: GetProductInfoResponse['data']['currentSeller'];
  shipments: {
    isFetched: boolean;
    isFetching: boolean;
    data: GetShipmentsResponse['data'];
  };
}

interface ProductActions {
  setCurrentSeller: (value: Seller) => void;
  setShipments: (value: {
    isFetched: boolean;
    isFetching: boolean;
    data: GetShipmentsResponse['data'];
  }) => void;
}

export type ProductStore = ProductState & ProductActions;

interface ProductStoreProviderProps extends PropsWithChildren {
  initialState: ProductState;
}

const { Provider, useContext } = createContext<StoreApi<ProductStore>>({
  name: '@store/catalog/product',
});

export function useProductStore<T>(selector: (store: ProductStore) => T) {
  const api = useContext();

  return useStoreWithEqualityFn(api, selector, shallow);
}

export function ProductStoreProvider(
  props: Readonly<ProductStoreProviderProps>
) {
  const { children, initialState } = props;

  const storeRef = useRef(
    createStore<ProductStore>()((set) => ({
      ...initialState,
      setCurrentSeller: (value) =>
        set((state) => ({
          ...state,
          currentSeller: value,
        })),
      setShipments: (value) =>
        set((state) => ({
          ...state,
          shipments: {
            ...state.shipments,
            isFetching: value.isFetching,
            isFetched: value.isFetched,
            data: value.data,
          },
        })),
    }))
  );

  return <Provider value={storeRef.current}>{children}</Provider>;
}
